import { useContext, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import {
  InterpolatedTranslation,
  Link,
  Loadable,
  Pane,
  Paragraph,
} from "components/materials";
import {
  PaymentsContext,
  PaymentsContextProvider,
} from "contexts/paymentsContext";
import t from "helpers/translate";
import { formatCurrency } from "helpers/formatCurrency";
import { VENDOR_PAYMENT_TYPE } from "helpers/enums";
import { minorScale, toaster } from "helpers/utilities";
import { get, has, values } from "lodash";
import { NexusErrorModal } from "./NexusErrorModal";
import { PostPaymentForm } from "./PostPaymentForm";
import { POST_PAYMENTS_TO_NEXUS } from "./graphql";

function getWarningTooltip({
  updatedSincePosted,
  documentHasVendorId,
  projectHasCustomId,
  projectHasGlCode,
}) {
  if (updatedSincePosted)
    return t("paymentIntegration.updatedSincePostedTooltip");
  if (!documentHasVendorId)
    return t("paymentIntegration.documentMissingVendorId");
  if (!projectHasCustomId)
    return t("paymentIntegration.missingProjectCustomId");
  if (!projectHasGlCode) return t("paymentIntegration.missingGlCode");
  return undefined;
}

function getRowState({
  updatedSincePosted,
  documentHasVendorId,
  projectHasCustomId,
  projectHasGlCode,
}) {
  if (updatedSincePosted) return "error";
  if (!documentHasVendorId) return "pending";
  if (!projectHasCustomId) return "pending";
  if (!projectHasGlCode) return "pending";
  return undefined;
}

function getCanBePosted({
  documentHasVendorId,
  documentHasBeenPosted,
  projectHasCustomId,
  projectHasGlCode,
}) {
  return (
    !documentHasBeenPosted &&
    documentHasVendorId &&
    projectHasCustomId &&
    projectHasGlCode
  );
}

/* build up an array of error objects for display in an informational modal
[
  {
    documentName: "Document_1.pdf", 
    errors: ["error message 1", "error message 2"...]
  }
]
*/
function formatErrors({ graphQLErrors, documents }) {
  const errorsByDocument = graphQLErrors.reduce(
    (errors, { details: documentId, message }) => {
      if (!has(errors, documentId)) {
        const document = documents.find(({ id }) => id === documentId);
        const documentName = get(document, "file.name");

        errors[documentId] = { documentName, errors: [] };
      }
      const errorMessage = message.includes(" => ")
        ? message.split(" => ")[1]
        : message;

      errors[documentId].errors.push(errorMessage);

      return errors;
    },
    {}
  );
  return values(errorsByDocument);
}

function InnerNexusPaymentPage({ drawId, projectId }) {
  const {
    glCode,
    queryLoading,
    documentsToPost,
    setDocumentsToPost,
    onPaymentPosted,
  } = useContext(PaymentsContext);

  const [returnedNexusErrors, setReturnedNexusErrors] = useState([]);

  const [postPaymentsToNexus, { loading: postPaymentsLoading }] = useMutation(
    POST_PAYMENTS_TO_NEXUS,
    {
      onCompleted: () => {
        setDocumentsToPost([]);
        onPaymentPosted();
        toaster.success("Your payments have successfully posted.", {
          duration: 2.5,
        });
      },
      onError: ({ graphQLErrors }) => {
        setDocumentsToPost([]);
        setReturnedNexusErrors(
          formatErrors({ documents: documentsToPost, graphQLErrors })
        );
      },
    }
  );

  function postPayments(formValues) {
    const payments = Object.entries(formValues.payments).reduce(
      (acc, [id, vendorPaymentType]) => {
        const document = documentsToPost.find((document) => document.id === id);
        if (!document || document.postedAt) return acc;

        return [
          ...acc,
          {
            documentId: id,
            amount: formatCurrency(document.paymentDueAmount),
            vendorPaymentType,
          },
        ];
      },
      []
    );

    postPaymentsToNexus({
      variables: {
        drawId,
        projectId,
        payments,
      },
    });
  }

  if (queryLoading) return <Loadable loading />;

  if (returnedNexusErrors.length !== 0)
    return (
      <NexusErrorModal
        nexusErrors={returnedNexusErrors}
        onCloseComplete={() => {
          setReturnedNexusErrors([]);
          onPaymentPosted();
        }}
      />
    );

  return (
    <PostPaymentForm
      confirmationMessage={
        <Pane>
          <Paragraph fontWeight={500} marginBottom={minorScale(1)}>
            {t("paymentIntegration.nexusCodes")}
          </Paragraph>
          <Paragraph>
            {t("paymentIntegration.nexusGlCode", { glCode })}
          </Paragraph>
          <InterpolatedTranslation
            string={t("paymentIntegration.nexusConfigurationProjectSettings1")}
            values={[
              <Link
                to={`/projects/${projectId}/settings?settings=nexusConfiguration`}
                purpose="nexus project settings set-nexus-configuration"
              >
                {t("paymentIntegration.nexusConfigurationProjectSettings2")}
              </Link>,
            ]}
          />
        </Pane>
      }
      accountsPayableSystem="Nexus"
      handleSubmit={postPayments}
      onSubmitLoading={postPaymentsLoading}
      vendorPaymentTypes={[
        VENDOR_PAYMENT_TYPE.SOFT_COSTS_DIRECT_PAY,
        VENDOR_PAYMENT_TYPE.SUBCONTRACTOR_REIMBURSABLE,
      ]}
    />
  );
}

export function NexusPaymentPage({ match }) {
  const { drawId, projectId } = match.params;

  return (
    <PaymentsContextProvider
      drawId={drawId}
      projectId={projectId}
      includeLineItems
      includeJobCostCodes={false}
      getWarningTooltip={getWarningTooltip}
      getRowState={getRowState}
      getCanBePosted={getCanBePosted}
    >
      <InnerNexusPaymentPage drawId={drawId} projectId={projectId} />
    </PaymentsContextProvider>
  );
}
